// eslint-disable-next-line no-unused-vars
import React from 'react';
import { object } from 'prop-types';
import styled from '@emotion/styled';
import { Button, SkeletonText } from '@carbon/react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import { Calendar, MapPinned, SquareUserRound, Activity, ClockAlert } from 'lucide-react';

import useNumberUtils from 'Hooks/useNumberUtils';
import getAwbPath from 'Utils/getAwbPath';

const statusColors = {
  PROCESSING: '#1893d0',
  STOCK_RESERVATION: '#68a765',
  DELIVERY_READY: '#01a982',
  EXPIRED: '#a67c52',
  BLOCKED: '#e8720b',
  DELIVERED: '#68a765',
  NOT_ACCEPTED: '#d52b1e',
  TRANSIT: '#67ccff',
  ACCEPTED: '#67ccff',
};

const StyledContainer = styled.div`
  display: flex;
  width: 100%;
  height: 130px;
  margin-bottom: 30px;
  background-color: #FBFBFB;
  border: 1px solid ${ props => props.theme.products.colors.border };

  .status {
    .value {
      color: ${ props => statusColors[props.status] || 'inherit' };
    }
  }

  .box {
    width: 25%;
    border-right: 1px solid ${ props => props.theme.products.colors.border };
    display: flex;
    align-items: center;
    justify-content: center;
    flex-grow: 1;

    :last-of-type {
      border-right: 0;
    }

    svg {
      margin-right: 10px;
    }

    //.cds--skeleton__text {
    //  max-width: 35%;
    //}

    > div {
      display: flex;
      flex-direction: column;

      .cds--btn {
        font-size: 1.25rem;
        color: ${ props => props.theme.colors.blue };
      }
    }
  }

  p {
    font-size: 1.25rem;
    text-align: left;
    color: ${ props => props.theme.colors.darkGray };

    &.title {
      font-weight: 600;
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    height: auto;
    flex-direction: column;

    .box {
      width: 100%;
      border-right: 0;
      border-bottom: 1px solid ${ props => props.theme.products.colors.border };
      padding: 20px 20px 20px 40px;
      justify-content: flex-start;

      :last-of-type {
        border-bottom: 0;
      }
    }
  }
`;

const OrderTooltip = ({ order }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { formatDate } = useNumberUtils();
  const handleOnScrollTo = e => {
    e.preventDefault();
    if (typeof document !== 'undefined') {
      const node = document.getElementById('orderAWBs');
      const payload = {
        left: 0,
        behavior: 'smooth'
      };
      if (node) {
        payload.top = node.offsetTop - 135;
      } else {
        payload.bottom = 200;
      }
      window.scrollTo(payload);
    }
  };
  return (
    <StyledContainer theme={ theme } status={ order.status }>
      <div className="box">
        <Calendar size={ 40 } />
        <div>
          <p className="title">{ t('order.orderDate') }</p>
          { ('added' in order)
            ? <p className="value">{ formatDate(order.added || order.date) }</p>
            : <SkeletonText />
          }
        </div>
      </div>
      <div className="box status">
        <Activity size={ 40 } />
        <div>
          <p className="title">{ t('order.status') }</p>
          { ('statusName' in order)
            ? <p className="value">{ order?.statusName }</p>
            : <SkeletonText />
          }
        </div>
      </div>
      <div className="box">
        <SquareUserRound size={ 40 } />
        <div>
          <p className="title">{ t('order.sentBy') }</p>
          { ('senderName' in order)
            ? <p className="value">{ order?.senderName }</p>
            : <SkeletonText />
          }
        </div>
      </div>
      { order.statusId !== 2 && (
        <div className="box">
          <MapPinned size={ 40 } />
          <div>
            <p className="title">
              { order?.awbs?.length > 1
                ? t('order.multipleAWBs')
                : t('order.awbNumber')
              }
            </p>
            { order.awbsDetails
              ? (
                <p className="values">
                  {
                    // eslint-disable-next-line no-nested-ternary
                    !order?.awbsDetails?.length
                      ? <span>{ t('common.unavailable') }</span>
                      : (
                        order?.awbsDetails?.length > 1
                          ? (
                            <Button kind="ghost" type="button" onClick={ handleOnScrollTo }>
                              { t('order.seeAllAWBS') }
                            </Button>
                          )
                          : (
                            <span>
                              <a
                                href={
                                  getAwbPath(
                                    order.awbsDetails[0].awbNumber,
                                    order.awbsDetails[0].transporterName
                                  )
                                }
                                target="_blank"
                              >
                                { order.awbsDetails[0].awbNumber }
                              </a>
                            </span>
                          )
                      )
                  }
                </p>
              )
              : <SkeletonText />
            }
          </div>
        </div>
      ) }
      { (order.statusId === 2 && 'expirationDate' in order) && (
        <div className="box">
          <ClockAlert size={ 40 } color="#D52B1E" />
          <div>
            <p className="title">{ t('order.expirationDate') }</p>
            { order.expirationDate
              ? <p className="value">{ order.expirationDate }</p>
              : <SkeletonText />
            }
          </div>
        </div>
      ) }
    </StyledContainer>
  );
};

OrderTooltip.propTypes = {
  order: object,
};
OrderTooltip.displayName = 'OrderTooltipComponent';

export default OrderTooltip;
